import { useEffect } from "react";
/** Glide starts moving a column once the pointer has travelled this far from the press. */
const DRAG_THRESHOLD = 20;
const OPACITY = "0.5";
const REFUSED_OPACITY = "0.2";
/** The column's slice of one canvas layer, and where it lands below the top of the grid, in canvas pixels. */
export const ghostSlice = (column, layer, grid, scale) => ({
    sx: (column.x - layer.x) * scale,
    sy: 0,
    sw: column.width * scale,
    sh: layer.height * scale,
    dy: (layer.y - grid.y) * scale,
});
export const ghostLeft = (start, dx, grid, width) => Math.max(grid.left, Math.min(grid.right - width, start + dx));
/** Glide paints the cells on its main canvas and the header on an overlay canvas right after it. */
const createGhost = (source, column) => {
    const box = source.getBoundingClientRect();
    const scale = box.width > 0 ? source.width / box.width : 1;
    const ghost = document.createElement("canvas");
    ghost.dataset.artisColumnGhost = "";
    ghost.width = Math.max(1, Math.round(column.width * scale));
    ghost.height = Math.max(1, Math.round(box.height * scale));
    const ctx = ghost.getContext("2d");
    const overlay = source.nextElementSibling;
    for (const layer of overlay instanceof HTMLCanvasElement ? [source, overlay] : [source]) {
        const { sx, sy, sw, sh, dy } = ghostSlice(column, layer.getBoundingClientRect(), box, scale);
        if (sw > 0 && sh > 0)
            ctx?.drawImage(layer, sx, sy, sw, sh, 0, dy, sw, sh);
    }
    Object.assign(ghost.style, {
        position: "fixed",
        top: `${box.y}px`,
        left: `${column.x}px`,
        width: `${column.width}px`,
        height: `${box.height}px`,
        opacity: OPACITY,
        pointerEvents: "none",
        zIndex: "9999",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.25)",
    });
    return { ghost, grid: { left: box.x, right: box.x + box.width } };
};
/**
 * While a header is dragged to move its column, a see-through copy of the column, as painted,
 * follows the pointer sideways; it fades while the move would be refused. The copy is taken on the
 * press, before Glide starts moving or highlighting the column.
 */
export const useColumnGhost = (wrapper, enabled, headerRef, columnRef, allowedRef) => {
    useEffect(() => {
        if (!wrapper || !enabled)
            return;
        let press = null;
        let shown = false;
        const end = () => {
            press?.copy.ghost.remove();
            press = null;
            shown = false;
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", end);
            window.removeEventListener("keydown", onKey);
        };
        const onMove = (e) => {
            if (!press)
                return;
            const dx = e.clientX - press.x;
            const { header, copy } = press;
            if (!shown) {
                if (Math.abs(dx) <= DRAG_THRESHOLD)
                    return;
                document.body.append(copy.ghost);
                shown = true;
            }
            copy.ghost.style.left = `${ghostLeft(header.bounds.x, dx, copy.grid, header.bounds.width)}px`;
            const target = columnRef.current ?? header.col;
            copy.ghost.style.opacity = allowedRef.current(header.col, target) ? OPACITY : REFUSED_OPACITY;
        };
        const onKey = (e) => {
            if (e.key === "Escape")
                end();
        };
        const onDown = (e) => {
            const header = headerRef.current;
            const source = wrapper.querySelector("[data-testid='data-grid-canvas']");
            if (e.button !== 0 || !header || header.edge || header.col < 0 || !source)
                return;
            end();
            press = { header, x: e.clientX, copy: createGhost(source, header.bounds) };
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", end);
            window.addEventListener("keydown", onKey);
        };
        wrapper.addEventListener("mousedown", onDown, { capture: true });
        return () => {
            wrapper.removeEventListener("mousedown", onDown, { capture: true });
            end();
        };
    }, [wrapper, enabled, headerRef, columnRef, allowedRef]);
};
//# sourceMappingURL=columnGhost.js.map